import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { marketing, type Campaign, type CampaignCreate } from "@/api/marketing";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

/**
 * Marketing campaigns — drip / announcement emails sent by MarketingDaemon.
 * Author the subject + body template and toggle enabled; the daemon handles
 * delivery against opted-in subscribers.
 */
export function AdminMarketingPage() {
  const list = useQuery({ queryKey: ["campaigns"], queryFn: () => marketing.list() });
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const toggle = useMutation({
    mutationFn: (c: Campaign) => marketing.patch(c.id, { enabled: !c.enabled }),
    onSuccess: () => {
      toast.push("Campaign updated", "success");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Marketing campaigns</h1>
        <Button onClick={() => setCreating(true)}>New campaign</Button>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No campaigns" description="Author drip and announcement emails for your clients." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Subject</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => (
                  <tr key={c.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-medium text-term-fg">{c.name}</td>
                    <td className="px-3 py-1.5 text-term-dim">{c.subject}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{c.kind}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={c.enabled ? "good" : "neutral"}>{c.enabled ? "Live" : "Paused"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setEditing(c)}>
                          Edit
                        </Button>
                        <Button variant="ghost" onClick={() => toggle.mutate(c)} disabled={toggle.isPending}>
                          {c.enabled ? "Pause" : "Enable"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {creating && <CampaignModal onClose={() => setCreating(false)} />}
      {editing && <CampaignModal campaign={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CampaignModal({ campaign, onClose }: { campaign?: Campaign; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<CampaignCreate>({
    name: campaign?.name ?? "",
    subject: campaign?.subject ?? "",
    body_template: campaign?.body_template ?? "",
    kind: campaign?.kind ?? "announcement",
    enabled: campaign?.enabled ?? false,
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => (campaign ? marketing.patch(campaign.id, form) : marketing.create(form)),
    onSuccess: () => {
      toast.push(campaign ? "Campaign updated" : "Campaign created", "success");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title={campaign ? `Edit · ${campaign.name}` : "New campaign"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="announcement">Announcement</option>
              <option value="drip">Drip</option>
              <option value="newsletter">Newsletter</option>
            </Select>
          </div>
        </div>
        <div>
          <Label>Subject</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </div>
        <div>
          <Label>Body template</Label>
          <textarea
            value={form.body_template}
            onChange={(e) => setForm({ ...form, body_template: e.target.value })}
            rows={6}
            className="w-full rounded-sm border border-term-border bg-term-bg px-2 py-1.5 text-xs text-term-fg focus:border-brand focus:outline-none"
            placeholder="Supports {{ placeholders }} resolved by the daemon."
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {campaign ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
